import { useApolloClient } from '@apollo/client';
import { Form, message } from 'antd';
import { Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import {
    type UpdateCtsModuleMutation,
    type UpdateCtsModuleMutationVariables,
    UpdateCtsModuleDocument,
} from '../../../../api/mutations/updateCtsModule';
import { type CtsModuleUpdateSettings } from '../../../../api/types';
import InputField from '../../../../components/fields/InputField';
import useHandleError from '../../../../utilities/useHandleError';
import useValidator from '../../../../utilities/useValidator';
import validators from '../../../../utilities/validators';

type CtsModuleUpdateFormProps = {
    moduleId: string;
    initialValues: CtsModuleUpdateSettings;
    onComplete: () => void;
};

const loadingMessageKey = 'primary';

const CtsModuleUpdateForm = ({ moduleId, initialValues, onComplete }: CtsModuleUpdateFormProps) => {
    const { t } = useTranslation(['ctsModuleDetails', 'moduleDetails']);

    const apolloClient = useApolloClient();

    const onSubmit = useHandleError(
        async (values: CtsModuleUpdateSettings) => {
            message.loading({
                content: t('ctsModuleDetails:messages.updateMainDetailsSubmitting'),
                key: loadingMessageKey,
                duration: 0,
            });

            await apolloClient
                .mutate<UpdateCtsModuleMutation, UpdateCtsModuleMutationVariables>({
                    mutation: UpdateCtsModuleDocument,
                    variables: { moduleId, settings: values },
                })
                .finally(() => {
                    message.destroy(loadingMessageKey);
                });

            message.success({
                content: t('ctsModuleDetails:messages.updateMainDetailsSuccessful'),
                key: 'primary',
            });

            // close the drawer
            onComplete();
        },
        [moduleId, apolloClient, onComplete, t]
    );

    const validate = useValidator(validators.requiredNonEmptyString('displayName'));

    return (
        <Formik<CtsModuleUpdateSettings> initialValues={initialValues} onSubmit={onSubmit} validate={validate}>
            {({ handleSubmit }) => (
                <Form id="updateCtsModule" layout="vertical" name="updateCtsModule" onSubmitCapture={handleSubmit}>
                    <InputField
                        {...t('moduleDetails:fields.displayName', {
                            returnObjects: true,
                        })}
                        name="displayName"
                        required
                    />
                </Form>
            )}
        </Formik>
    );
};

export default CtsModuleUpdateForm;
